import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, ClipboardList, Sparkles, Bot, Calendar, BarChart2, BookOpen, Settings, User, LogOut, ChevronDown, Menu, X, Shield, Calculator, TrendingUp, Flame, LayoutGrid } from 'lucide-react';
import { logout } from '../api';
import { colors, font, radius } from '../styles/theme';

const NAV_ITEMS = [
  { label: '오늘의공고', path: '/today', icon: Flame },
  { label: '공고목록', path: '/announcements', icon: ClipboardList },
  { label: 'AI추천', path: '/recommend', icon: Sparkles },
  { label: 'AI챗봇', path: '/chat', icon: Bot },
];

const MORE_ITEMS = [
  // 통합 로그인 뒤 첫 화면. 다른 앱(AI공취 등)으로 건너가는 유일한 진입점이라 맨 앞에 둔다.
  { label: '앱 전환', path: '/apps', icon: LayoutGrid },
  { label: '청약 전략', path: '/strategy', icon: TrendingUp },
  { label: '캘린더', path: '/calendar', icon: Calendar },
  { label: '공고비교', path: '/compare', icon: BarChart2 },
  { label: '가점계산기', path: '/score', icon: Calculator },
  { label: '이용가이드', path: '/guide', icon: BookOpen },
  { label: '내 조건', path: '/mypage/condition', icon: Settings },
  { label: '마이페이지', path: '/mypage', icon: User },
];

// 모바일 하단 탭 (5개 핵심 메뉴)
const BOTTOM_TABS = [
  // 숲푸딩 안의 홈은 대시보드. 루트(/)는 로그인 시 앱 선택이라 여기서 가리키지 않는다.
  { label: '홈', path: '/dashboard', icon: Home, end: true },
  { label: '공고', path: '/announcements', icon: ClipboardList },
  { label: '추천', path: '/recommend', icon: Sparkles },
  { label: '챗봇', path: '/chat', icon: Bot },
  { label: 'MY', path: '/mypage', icon: User, end: true },
];

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [menuOpen, setMenuOpen] = useState(false);
  const [winW, setWinW] = useState(window.innerWidth);
  const isMobile = winW <= 768;
  const isNarrow = winW <= 1000; // 데스크톱이지만 좁은 창 — 닉네임 숨김
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWinW(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const handleLogout = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) logout(refreshToken);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const moreItems = [
    ...MORE_ITEMS,
    ...(user?.role === 'ADMIN' ? [{ label: '관리자', path: '/admin', icon: Shield }] : []),
  ];

  const navLinkStyle = ({ isActive }) => ({
    ...styles.navLink,
    background: isActive ? colors.primaryLight : 'transparent',
    color: isActive ? colors.primaryDark : colors.grey700,
    fontWeight: isActive ? font.bold : font.medium,
  });

  return (
    <>
      {/* 본문 바로가기 — 목록 페이지는 첫 결과까지 탭 62번이라 키보드 사용자에게 필수다.
          display:none이 아니라 화면 밖 배치를 쓴다. display:none은 스크린리더가 읽지 못한다.
          탭 순서상 가장 먼저 와야 하므로 위치를 옮기지 말 것. */}
      <a href="#main" className="skip-link">본문 바로가기</a>
      <header style={styles.header}>
        {/* 로고 */}
        {/* 홈이 비로그인에도 공고 목록을 보여주므로 모두 같은 곳으로 보낸다 */}
        <Link to="/" style={styles.logoWrap} aria-label="SooPooDing 홈">
          <img src="/logo.svg" alt="" width="32" height="32" style={{ borderRadius: '9px', display: 'block' }} />
          <span style={styles.logoText}>SooPooDing</span>
        </Link>

        {isMobile ? (
          /* 모바일: 햄버거 (로그인) or 상단 링크 (비로그인) */
          <div ref={menuRef}>
            {user ? (
              <>
                <button
                  ref={menuButtonRef}
                  style={styles.hamburger}
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-expanded={menuOpen}
                  aria-label={menuOpen ? '메뉴 닫기' : '메뉴 열기'}
                >
                  {menuOpen ? <X size={24} color={colors.grey800} /> : <Menu size={24} color={colors.grey800} />}
                </button>
                {menuOpen && (
                  <nav style={styles.mobileMenu} aria-label="전체 메뉴">
                    <div style={styles.mobileUserBox}>
                      <div style={styles.mobileAvatar}>{user.nickname?.charAt(0) || '?'}</div>
                      <div>
                        <p style={styles.mobileNickname}>{user.nickname}님</p>
                        <p style={styles.mobileEmail}>{user.email}</p>
                      </div>
                    </div>
                    <div style={styles.mobileDivider} />
                    {moreItems.map(item => {
                      const Icon = item.icon;
                      const active = location.pathname === item.path;
                      return (
                        <Link key={item.path} to={item.path}
                          style={{ ...styles.mobileLink, background: active ? colors.primaryLight : 'transparent', color: active ? colors.primaryDark : colors.grey800 }}
                        >
                          <Icon size={20} color={active ? colors.primaryDark : colors.grey600} />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                    <div style={styles.mobileDivider} />
                    <button style={styles.mobileLogout} onClick={handleLogout}>
                      <LogOut size={20} color={colors.redText} />
                      <span>로그아웃</span>
                    </button>
                  </nav>
                )}
              </>
            ) : (
              <nav style={{ display: 'flex', gap: '4px', alignItems: 'center' }} aria-label="주요 메뉴">
                <NavLink to="/announcements" style={navLinkStyle}>공고</NavLink>
                <NavLink to="/guide" style={navLinkStyle}>가이드</NavLink>
                <Link to="/login" style={styles.loginBtn}>로그인</Link>
              </nav>
            )}
          </div>
        ) : (
          /* PC 네비게이션 */
          <nav style={styles.desktopNav} aria-label="주요 메뉴">
            {user ? (
              <>
                {NAV_ITEMS.map(item => {
                  const Icon = item.icon;
                  return (
                    <NavLink key={item.path} to={item.path} style={navLinkStyle}>
                      <Icon size={18} style={{ marginRight: 5 }} aria-hidden="true" />
                      {item.label}
                    </NavLink>
                  );
                })}

                {/* 더보기 드롭다운 */}
                <div style={styles.dropdownWrap} ref={menuRef}>
                  <button
                    ref={menuButtonRef}
                    style={{ ...styles.moreBtn, background: menuOpen ? colors.grey100 : 'transparent' }}
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-expanded={menuOpen}
                    aria-haspopup="true"
                  >
                    더보기 <ChevronDown size={16} style={{ marginLeft: 3 }} aria-hidden="true" />
                  </button>
                  {menuOpen && (
                    <div style={styles.dropdown} role="menu">
                      {moreItems.map(item => {
                        const Icon = item.icon;
                        const active = location.pathname === item.path;
                        return (
                          <Link key={item.path} to={item.path} role="menuitem"
                            style={{ ...styles.dropdownItem, background: active ? colors.primaryLight : 'transparent', color: active ? colors.primaryDark : colors.grey800 }}
                          >
                            <Icon size={18} color={active ? colors.primaryDark : colors.grey600} aria-hidden="true" />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={styles.navDivider} />
                {!isNarrow && <span style={styles.nickname}>{user.nickname}님</span>}
                <button style={styles.logoutBtn} onClick={handleLogout}>
                  <LogOut size={16} style={{ marginRight: 4 }} aria-hidden="true" />로그아웃
                </button>
              </>
            ) : (
              <>
                <NavLink to="/today" style={navLinkStyle}>
                  <Flame size={18} style={{ marginRight: 5 }} aria-hidden="true" />오늘의공고
                </NavLink>
                <NavLink to="/announcements" style={navLinkStyle}>
                  <ClipboardList size={18} style={{ marginRight: 5 }} aria-hidden="true" />공고목록
                </NavLink>
                <NavLink to="/guide" style={navLinkStyle}>
                  <BookOpen size={18} style={{ marginRight: 5 }} aria-hidden="true" />이용가이드
                </NavLink>
                <div style={styles.navDivider} />
                <Link to="/login" style={styles.loginBtn}>로그인</Link>
                <Link to="/signup" style={styles.signupBtn}>회원가입</Link>
              </>
            )}
          </nav>
        )}
      </header>

      {/* 모바일 하단 탭바 */}
      {isMobile && user && (
        <nav style={styles.bottomTab} aria-label="하단 탭">
          {BOTTOM_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <NavLink key={tab.path} to={tab.path} end={tab.end} style={styles.tabItem}>
                {({ isActive }) => (
                  <>
                    <div style={{ ...styles.tabIconWrap, background: isActive ? colors.primaryLight : 'transparent' }}>
                      <Icon size={22} color={isActive ? colors.primaryDark : colors.grey500} strokeWidth={isActive ? 2.5 : 1.5} aria-hidden="true" />
                    </div>
                    <span style={{ ...styles.tabLabel, color: isActive ? colors.primaryDark : colors.grey600, fontWeight: isActive ? font.bold : font.regular }}>
                      {tab.label}
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      )}
    </>
  );
}

const styles = {
  header: {
    background: colors.white,
    padding: '0 20px',
    height: '60px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 200,
    borderBottom: `1px solid ${colors.grey200}`,
  },
  logoWrap: { display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', padding: '6px 0' },
  logoIcon: {
    width: '32px', height: '32px', background: colors.primary,
    borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  logoText: { color: colors.grey900, fontSize: '17px', fontWeight: font.bold, letterSpacing: '-0.5px' },

  // PC 네비
  desktopNav: { display: 'flex', alignItems: 'center', gap: '2px' },
  navLink: {
    display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
    padding: '10px 12px', minHeight: '44px', borderRadius: radius.sm,
    fontSize: '14px', textDecoration: 'none',
    transition: 'background 0.15s, color 0.15s',
  },
  moreBtn: {
    display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
    padding: '10px 12px', borderRadius: radius.sm,
    fontSize: '14px', fontWeight: font.medium,
    color: colors.grey700, border: 'none', cursor: 'pointer',
  },
  dropdownWrap: { position: 'relative' },
  dropdown: {
    position: 'absolute', top: '46px', right: 0,
    background: colors.white, borderRadius: '14px',
    boxShadow: '0 8px 24px rgba(2,6,23,0.12)',
    minWidth: '170px', overflow: 'hidden', zIndex: 300,
    padding: '6px',
  },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '11px 14px', fontSize: '14px',
    borderRadius: radius.sm, textDecoration: 'none',
  },
  navDivider: { width: '1px', height: '20px', background: colors.grey200, margin: '0 8px', flexShrink: 0 },
  nickname: {
    color: colors.grey600, fontSize: '13px', whiteSpace: 'nowrap',
    maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  loginBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '44px', background: colors.primaryDark, color: colors.white,
    borderRadius: radius.sm, padding: '0 16px',
    fontSize: '14px', fontWeight: font.bold, textDecoration: 'none',
    whiteSpace: 'nowrap', flexShrink: 0,
  },
  signupBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '44px', background: colors.white, color: colors.primaryDark,
    border: `1.5px solid ${colors.grey300}`,
    borderRadius: radius.sm, padding: '0 14px',
    fontSize: '14px', fontWeight: font.semibold, textDecoration: 'none',
    whiteSpace: 'nowrap', flexShrink: 0,
  },
  logoutBtn: {
    background: 'transparent', color: colors.grey600,
    border: `1px solid ${colors.grey300}`,
    borderRadius: radius.sm, padding: '8px 12px', fontSize: '13px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0,
  },

  // 모바일 햄버거
  hamburger: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    padding: '10px', display: 'flex', alignItems: 'center',
  },
  mobileMenu: {
    position: 'fixed', top: '60px', left: 0, right: 0, bottom: 0,
    background: colors.white, overflowY: 'auto', zIndex: 199,
    display: 'flex', flexDirection: 'column',
  },
  mobileUserBox: { display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 24px' },
  mobileAvatar: {
    width: '44px', height: '44px', background: colors.primaryLight,
    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '20px', fontWeight: font.bold, color: colors.primaryDark, flexShrink: 0,
  },
  mobileNickname: { color: colors.grey900, fontSize: '16px', fontWeight: font.bold, margin: '0 0 2px' },
  mobileEmail: { color: colors.grey600, fontSize: '13px', margin: 0 },
  mobileDivider: { height: '1px', background: colors.grey200, margin: '4px 0' },
  mobileLink: {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '16px 24px', fontSize: '16px', textDecoration: 'none',
    borderBottom: `1px solid ${colors.grey100}`,
  },
  mobileLogout: {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '16px 24px', fontSize: '16px', color: colors.redText,
    background: 'transparent', border: 'none', cursor: 'pointer',
    width: '100%', textAlign: 'left',
  },

  // 하단 탭바
  bottomTab: {
    position: 'fixed', bottom: 0, left: 0, right: 0,
    height: '64px', background: colors.white,
    borderTop: `1px solid ${colors.grey200}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-around',
    zIndex: 199, boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  tabItem: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '2px',
    padding: '8px 0', textDecoration: 'none',
  },
  tabIconWrap: {
    width: '36px', height: '36px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.2s',
  },
  tabLabel: { fontSize: '12px', lineHeight: 1 },
};

export default Header;
